import { useState, useEffect, useRef, useCallback } from 'react';
import { usePlatform } from '../../context/PlatformContext';
import { useAuth } from '../../context/AuthContext';
import { metricsApi } from '../../services/api';

const PLATFORMS = [
  { key: 'all', label: 'All Platforms', icon: '🌐' },
  { key: 'mobile', label: 'Android Mobile', icon: '📱' },
  { key: 'atv', label: 'Android TV', icon: '📺' },
  { key: 'firestick', label: 'FireStick', icon: '🔥' },
  { key: 'ios', label: 'iOS', icon: '🍎' },
];

const ENVIRONMENTS = [
  { key: 'production', label: 'Production', color: 'bg-emerald-500/20 text-emerald-400' },
  { key: 'beta', label: 'Beta', color: 'bg-amber-500/20 text-amber-400' },
  { key: 'internal', label: 'Internal', color: 'bg-blue-500/20 text-blue-400' },
];

function timeAgo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function GlobalHeader({ versions = [], onRefresh }) {
  const { platform, setPlatform, version, setVersion, environment, setEnvironment, viewMode, setViewMode } = usePlatform();
  const { user } = useAuth();

  const [refreshing, setRefreshing] = useState(false);
  const [refreshId, setRefreshId] = useState(null);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshStatus, setRefreshStatus] = useState(null); // 'running' | 'completed' | 'failed'
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncSchedule, setSyncSchedule] = useState(null);
  const pollRef = useRef(null);

  // Fetch sync status on mount
  useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const res = await metricsApi.getSyncStatus();
        const data = res.data;
        if (data.scheduler?.lastSyncTime) {
          setLastSyncTime(data.scheduler.lastSyncTime);
        }
        if (data.lastRefresh?.completed_at) {
          setLastSyncTime(data.lastRefresh.completed_at);
        }
        if (data.lastRefresh?.status === 'running') {
          // Resume polling if a refresh is already running
          setRefreshing(true);
          setRefreshId(data.lastRefresh.id);
          setRefreshProgress(data.lastRefresh.progress_pct || 0);
          setRefreshStatus('running');
        }
        setSyncSchedule(data.scheduler?.schedules);
      } catch {
        // silent fail
      }
    };
    fetchSyncStatus();
  }, []);

  // Poll refresh progress
  const startPolling = useCallback((id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await metricsApi.getStatus(id);
        const r = res.data.refresh;
        setRefreshProgress(r.progress_pct || 0);
        setRefreshStatus(r.status);

        if (r.status === 'completed' || r.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRefreshing(false);
          if (r.status === 'completed') {
            setLastSyncTime(r.completed_at || new Date().toISOString());
            // Reload dashboard data
            onRefresh?.();
          }
          // Clear status after 5s
          setTimeout(() => {
            setRefreshStatus(null);
            setRefreshProgress(0);
          }, 5000);
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRefreshing(false);
        setRefreshStatus('failed');
      }
    }, 3000);
  }, [onRefresh]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Trigger manual refresh
  const handleForceRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshProgress(0);
    setRefreshStatus('running');
    try {
      const res = await metricsApi.refresh();
      const id = res.data.refreshId;
      setRefreshId(id);
      startPolling(id);
    } catch (err) {
      setRefreshing(false);
      setRefreshStatus('failed');
      setTimeout(() => setRefreshStatus(null), 3000);
    }
  };

  const currentEnv = ENVIRONMENTS.find(e => e.key === environment) || ENVIRONMENTS[0];

  return (
    <div className="sticky top-0 z-30 bg-surface-800/95 backdrop-blur-sm border-b border-surface-600">
      {/* Row 1: Platform tabs + View toggle */}
      <div className="flex items-center justify-between px-4 py-2">
        {/* Platform tabs */}
        <div className="flex items-center gap-1">
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPlatform(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                platform === p.key
                  ? 'bg-brand-500/20 text-brand-400 ring-1 ring-brand-500/40'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-surface-700'
              }`}
            >
              <span className="mr-1.5">{p.icon}</span>
              <span className="hidden sm:inline">{p.label}</span>
            </button>
          ))}
        </div>

        {/* Right: View toggle + Environment + Sync */}
        <div className="flex items-center gap-3">
          {/* Environment badge */}
          <select
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            className={`text-xs font-medium px-2.5 py-1 rounded-full border-0 cursor-pointer ${currentEnv.color} bg-opacity-20`}
          >
            {ENVIRONMENTS.map((env) => (
              <option key={env.key} value={env.key} className="bg-surface-800 text-gray-200">{env.label}</option>
            ))}
          </select>

          {/* View mode toggle */}
          {(user?.role === 'admin' || user?.role === 'management') && (
            <div className="flex items-center bg-surface-700 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('executive')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'executive'
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                Executive
              </button>
              <button
                onClick={() => setViewMode('developer')}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                  viewMode === 'developer'
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-gray-400 hover:text-gray-200'
                }`}
              >
                Developer
              </button>
            </div>
          )}

          {/* Sync status + Refresh button */}
          <div className="flex items-center gap-2">
            {/* Last sync time */}
            {lastSyncTime && !refreshing && (
              <span className="text-[10px] text-gray-500" title={`Last synced: ${new Date(lastSyncTime).toLocaleString()}`}>
                Synced {timeAgo(lastSyncTime)}
              </span>
            )}

            {/* Progress indicator during refresh */}
            {refreshing && (
              <div className="flex items-center gap-1.5">
                <div className="w-16 h-1.5 bg-surface-600 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-500 rounded-full transition-all duration-500"
                    style={{ width: `${refreshProgress}%` }}
                  />
                </div>
                <span className="text-[10px] text-brand-400 font-mono">{Math.round(refreshProgress)}%</span>
              </div>
            )}

            {/* Completion/error flash */}
            {!refreshing && refreshStatus === 'completed' && (
              <span className="text-[10px] text-emerald-400 font-medium">Synced!</span>
            )}
            {!refreshing && refreshStatus === 'failed' && (
              <span className="text-[10px] text-red-400 font-medium">Sync failed</span>
            )}

            {/* Refresh dashboard data (light reload) */}
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-surface-700 transition-colors"
                title="Refresh dashboard"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}

            {/* Force sync from Firebase/BigQuery (admin only) */}
            {user?.role === 'admin' && (
              <button
                onClick={handleForceRefresh}
                disabled={refreshing}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  refreshing
                    ? 'bg-brand-500/10 text-brand-400 cursor-wait'
                    : 'bg-brand-500/20 text-brand-400 hover:bg-brand-500/30 hover:text-brand-300'
                }`}
                title="Force sync from Firebase & Google Play (fetches latest data from BigQuery)"
              >
                <svg className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {refreshing ? 'Syncing...' : 'Sync Now'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Row 2: Version selector + Auto-sync indicator */}
      <div className="flex items-center justify-between px-4 pb-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 uppercase tracking-wider">Version:</span>
          <select
            value={version || ''}
            onChange={(e) => setVersion(e.target.value || null)}
            className="bg-surface-700 text-sm text-gray-200 border border-surface-600 rounded-lg px-3 py-1.5 focus:ring-1 focus:ring-brand-500 focus:border-brand-500 min-w-[220px]"
          >
            {versions.length > 0 ? (
              <option value="">
                {`Latest release ${versions[0].app_version || versions[0].appVersion}${(versions[0].version_code || versions[0].versionCode) ? ` (${versions[0].version_code || versions[0].versionCode})` : ''}`}
              </option>
            ) : (
              <option value="">Select Version</option>
            )}
            {versions.slice(1).map((v) => {
              const ver = v.app_version || v.appVersion;
              const code = v.version_code || v.versionCode;
              const label = code ? `${ver} (${code})` : ver;
              return (
                <option key={`${ver}-${code}`} value={ver} className="bg-surface-800 text-gray-200">
                  {label}
                </option>
              );
            })}
          </select>

          {version && (
            <button
              onClick={() => setVersion(null)}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
            >
              Clear
            </button>
          )}
        </div>

        {/* Auto-sync schedule indicator */}
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="text-[10px] text-gray-600">
            Auto-sync every 4h
          </span>
        </div>
      </div>
    </div>
  );
}
